import { useEffect, useRef, useState } from 'react';
import { localeUI } from '../lib/format';
import { api, ApiError } from '../lib/api';
import { encolarEnvio } from '../lib/envioConDeshacer';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { adjusterTitleShort, type Adjuster } from './AdjusterInfoBox';
import { RichEditor, type RichEditorHandle } from './RichEditor';
import { textoAHtml } from '../lib/textoAHtml';
import { EmailThread, type EmailLike } from './email/EmailThread';
import { asuntoDeRespuesta, destinoDeRespuesta } from '../lib/emailThreads';
import { RecipientChips } from './RecipientChips';
import { InsuranceLink } from './InsuranceCard';
import { MailboxChannelBadge, type EstadoCanal } from './MailboxChannelBadge';
import { AttachFilesModal } from './AttachFilesModal';
import { TemplatePickerModal } from './TemplatePickerModal';
import { Home, Mail, Mailbox, Paperclip, Video, Wrench, X } from 'lucide-react';

type ClaimFile = {
  id: string; file_name: string | null; doc_type: string; file_key?: string | null;
  doc_label?: string | null; created_at?: string | null; amount_cents?: number | null;
  financials?: { is_ours?: boolean; rcv_amount_cents?: number | null } | null;
  supplement_status?: { kind: 'resolved' | 'pre_approved'; at: string; by: string | null } | null;
};
// CUALQUIER archivo del claim con nombre es adjuntable (reporte de Brenda: solo salían
// PDF/ESX y el equipo no podía adjuntar fotos/Word/Excel desde la vista del claim).
const isAttachable = (f: ClaimFile) => Boolean((f.file_name ?? '').trim());

/** Agrega un destinatario al campo "to" (coma-separado) sin duplicar (case-insensitive). */
function addRecipient(current: string, email: string): string {
  const list = current.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  if (list.some((x) => x.toLowerCase() === email.toLowerCase())) return current;
  return [...list, email].join(', ');
}

/**
 * Ventana de email UNIFICADA del claim (First Estimate / Mitigation / Review / Supplementing).
 * - 3 pestañas: Insurance / Contractor / Homeowner. Historial por claim (envíos + respuestas,
 *   con quién y cuándo) → se ve igual en todas las etapas.
 * - Subject sugiere el claim number.
 * - Chips de datos (Name/Address/Contractor/RCV/Claim #/Insurance): se insertan en el cursor
 *   del mensaje como una etiqueta con borde de color que ADENTRO tiene el valor real.
 * - Adjuntos con checkmark: estimado de suplemento y/o del seguro (se elige cuál mandar).
 */
type EmailRow = {
  id: string; direction: string; recipient_type: string | null;
  from_address: string | null; to_address: string | null; cc_address?: string | null; subject: string | null;
  body: string | null; status: string; created_at: string; author: string | null;
  delivered_at?: string | null; opened_at?: string | null; clicked_at?: string | null;
  attachments?: { id: string; file_name: string | null; mime_type: string | null }[];
};
type Tab = 'insurance' | 'contractor' | 'homeowner';
export type EmailTokens = { name?: string | null; contractor?: string | null; rcv?: string | null; insurance?: string | null };

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export function ClaimEmailBox({ claimId, alsoClaimIds, defaultTo, insuranceSource, claimNumber, tokens, canAttach, departmentKey, title, extraChips, hideHistory, atrVideo, preAttachFileIds, autoTemplate, prefill, beforeSend, onSent, templatesScope, side, defaultTab }: {
  claimId: string; alsoClaimIds?: string[]; defaultTo?: Partial<Record<Tab, string | null>>;
  /** De dónde salió `defaultTo.insurance`: 'kb' (buzón de reclamos de la ficha) · 'adjuster' (ajustador del claim) · 'kb_details' (correo general de la ficha). */
  insuranceSource?: string | null; claimNumber?: string | null;
  tokens?: EmailTokens; canAttach?: { supplement?: boolean; insurance?: boolean }; departmentKey?: string;
  title?: string; extraChips?: { label: string; value: string }[]; hideHistory?: boolean;
  /** Vista de trabajo dueña de este módulo: sus plantillas de email salen SOLO acá. */
  templatesScope?: { deptKey: string; viewKey: string };
  /** Contenido para la columna derecha (el hueco muerto): p. ej. el follow-up del depto. */
  side?: React.ReactNode;
  /** Pestaña inicial (COC arranca en Insurance, que es a quién se le manda). */
  defaultTab?: Tab;
  // ATR: si viene un video aceptado, su LINK se incluye en el cuerpo del email al enviar.
  atrVideo?: { url: string | null; label?: string | null } | null;
  // Archivos del claim a PRE-ADJUNTAR (ej. Mitigation + Rebuild estimate). Se agregan a los adjuntos.
  preAttachFileIds?: string[];
  /**
   * Nombre de la plantilla que se carga SOLA al abrir (ej. "First estimate"). Se busca por nombre
   * y no por id: los ids son de cada empresa. Solo se aplica si el cuerpo está VACÍO — un
   * automatismo que pisa lo que alguien escribió deja de ser una ayuda.
   */
  autoTemplate?: string;
  /**
   * UN EMAIL YA ESCRITO PARA ESTE CLAIM que se precarga en el editor: p. ej. el dispute email que
   * suplementos aprobó en Review Estimate y que el estimador manda con el estimado (dueño
   * 2026-10-07: "ese mismo email lo deben tener ellos allá para, después de subir el estimado,
   * enviarlo"). Se siembra solo si el cuerpo está VACÍO o si todavía tiene exactamente lo que se
   * sembró antes (se guarda la huella en localStorage): lo que una persona escribió a mano no se
   * pisa nunca. `text` es texto plano (se pasa a HTML como lo haría el servidor); `html` gana si viene.
   */
  prefill?: { subject?: string | null; text?: string | null; html?: string | null } | null;
  // Se llama tras un envío exitoso (con el tab destino) — Review ATR lo usa para avanzar a ATR Follow up.
  onSent?: (tab: Tab) => void;
  /** Se pregunta ANTES de enviar: si devuelve un texto, el email NO sale y ese texto se muestra. */
  beforeSend?: (tab: Tab) => Promise<string | null>;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>(defaultTab ?? 'insurance');
  // COPIA: el homeowner y/o el contratista reciben el mismo email sin ser el destinatario.
  const [ccHomeowner, setCcHomeowner] = useState(false);
  const [ccContractor, setCcContractor] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; subject: string | null; body_html: string; dept?: string | null }>>([]);
  const [tplOpen, setTplOpen] = useState(false); // la ventana de plantillas
  const [rows, setRows] = useState<EmailRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Vacío y fallido NO se ven igual (ley: `catch { setX([]) }` convierte un error en un vacío
  // creíble). Si el hilo no carga, se dice y se puede reintentar.
  const [fallo, setFallo] = useState(false);
  const [to, setTo] = useState('');
  // RESPONDER un correo del historial (dueño 2026-10-19): chip arriba del To, el envío lleva
  // `replyToEmailId` y sale encadenado (In-Reply-To/References). Ver ClaimComms para la historia.
  const [replyTo, setReplyTo] = useState<EmailLike | null>(null);
  const toPendiente = useRef<string | null>(null);
  // El SUBJECT sobrevive si el usuario navega y vuelve (pedido Paula 2026-08-14); el body ya
  // persiste vía draftKey del RichEditor. El To no: se auto-llena por tab y guardar uno viejo
  // pisaría el correcto.
  const [subject, setSubject] = usePersistentState(`qe:${claimId}:${departmentKey ?? 'main'}:subject`, claimNumber ?? '');
  const [busy, setBusy] = useState(false);
  /**
   * DESDE QUÉ DIRECCIÓN SALE ESTE EMAIL.
   *
   * 'sistema' = la cuenta del departamento (Resend) con el token reply+, que es lo que hace que la
   * respuesta vuelva sola al claim y a la pestaña correcta.
   * 'yo' = la casilla propia del empleado por su SMTP: al homeowner le llega un correo normal, de
   * una persona. Desde el cambio de hoy también queda pegado al hilo del claim, así que lo único
   * que se resigna es el ruteo automático de la respuesta.
   *
   * Solo aparece si la persona TIENE casilla configurada: ofrecer una opción que no se puede usar
   * es hacerle una pregunta que no puede responder.
   */
  const [desde, setDesde] = useState<'sistema' | 'yo'>('sistema');
  /**
   * ⚠️ ACÁ MIRA EL CANAL DE **ENVIAR**, NO EL SEMÁFORO ÚNICO.
   *
   * Esta es la pantalla donde se manda el correo, o sea la pantalla del caso de Paula. Leía
   * `last_check_ok !== false`, que es el booleano viejo que mezcla recibir con enviar: una casilla
   * que recibe perfecto y no manda nada salía en verde justo acá, y el ⚠ genérico no decía ni qué
   * canal ni de cuándo era el dato. Ahora se muestra el estado de SMTP con su fecha, igual que en
   * el resto de la app (mismo badge, misma regla).
   */
  const [miCasilla, setMiCasilla] = useState<{ email: string; smtp: EstadoCanal } | null>(null);
  useEffect(() => {
    api.get<{ mailbox: { email: string; has_password: boolean; smtp: EstadoCanal } | null }>('/api/mailbox')
      .then((r) => setMiCasilla(r.mailbox?.email && r.mailbox.has_password
        ? { email: r.mailbox.email, smtp: r.mailbox.smtp } : null))
      .catch(() => setMiCasilla(null));
  }, []);
  const [msg, setMsg] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [attachSup, setAttachSup] = useState(Boolean(canAttach?.supplement));
  const [attachIns, setAttachIns] = useState(false);
  const [files, setFiles] = useState<ClaimFile[]>([]); // PDF/ESX del claim (para adjuntar)
  const [attachIds, setAttachIds] = useState<string[]>([]); // archivos elegidos del dropdown
  const [uploading, setUploading] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false); // dropdown custom de adjuntar
  const [savingAtt, setSavingAtt] = useState<string | null>(null); // email cuyos adjuntos se están pegando a Files

  // Pega los adjuntos de un email a los Files del claim.
  async function addToClaimFiles(emailId: string) {
    setSavingAtt(emailId); setMsg(null);
    try {
      const r = await api.post<{ attached: number }>(`/api/emails/${emailId}/attach-to-claim`, {});
      setMsg(t('em.addedToFiles', { n: String(r.attached ?? 0) }));
    } catch (e) { setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error'); }
    finally { setSavingAtt(null); }
  }
  const [adjusters, setAdjusters] = useState<Adjuster[]>([]); // ajustadores del claim (para elegir email)
  const editorRef = useRef<RichEditorHandle>(null);

  useEffect(() => { api.get<{ adjusters: Adjuster[] }>(`/api/claims/${claimId}/adjusters`).then((r) => setAdjusters(r.adjusters)).catch(() => {}); }, [claimId]);

  // Plantillas de ESTA vista de trabajo (se crean en su ⚙). Solo salen acá.
  const tplDept = templatesScope?.deptKey;
  const tplView = templatesScope?.viewKey;
  /**
   * DOS ORÍGENES de plantillas y los dos se muestran juntos:
   *   · las de la VISTA (el engranaje de una cola de Payments) — solo si el composer está montado
   *     en una vista;
   *   · las de la EMPRESA (Settings → Plantillas de email), filtradas por el departamento de este
   *     buzón. Estas son las que arma el equipo para no pegar del Word, así que tienen que estar
   *     disponibles en TODOS lados, no solo dentro de una cola.
   */
  useEffect(() => {
    let alive = true;
    const deVista = tplDept && tplView
      ? api.get<{ templates: Array<{ id: string; name: string; subject: string | null; body_html: string }> }>(
          `/api/views/${encodeURIComponent(tplDept)}/${encodeURIComponent(tplView)}/settings`,
        ).then((r) => r.templates ?? []).catch(() => [])
      : Promise.resolve([]);
    // TODAS las plantillas de la empresa, agrupadas por departamento en el selector
    // (pedido 2026-08-11: "seleccione por departamento y salga el titular del template").
    const deEmpresa = api.get<{ templates: Array<{ id: string; label: string; subject: string; body_html: string; department_key: string | null }> }>(
      '/api/email-templates?all=1',
    ).then((r) => (r.templates ?? []).map((x) => ({
      // `id` con prefijo: las dos listas tienen ids que pueden chocar y el <select> elige por id.
      id: `co:${x.id}`, name: x.label, subject: x.subject || null, body_html: x.body_html, dept: x.department_key,
    }))).catch(() => []);
    void Promise.all([deVista, deEmpresa]).then(([a, b]) => { if (alive) setTemplates([...a, ...b]); });
    return () => { alive = false; };
  }, [tplDept, tplView, departmentKey]);
  // Fecha + HORA (pedido del equipo: la hora de llegada importa para trabajar los emails).
  const fmtDate = (iso: string) => new Date(iso).toLocaleString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });


  const loadFiles = () => api.get<{ files: ClaimFile[] }>(`/api/claims/${claimId}/files`)
    .then((r) => setFiles((r.files || []).filter(isAttachable))).catch(() => {});
  useEffect(() => { loadFiles(); /* eslint-disable-next-line */ }, [claimId]);

  // Pre-adjuntar archivos que pide el padre (ej. Mitigation + Rebuild estimate) SIN pisar lo que
  // el usuario ya agregó/quitó a mano: solo hace UNIÓN de los ids nuevos.
  const preKey = (preAttachFileIds ?? []).join(',');
  useEffect(() => {
    if (!preAttachFileIds || preAttachFileIds.length === 0) return;
    setAttachIds((cur) => {
      const set = new Set(cur); let changed = false;
      for (const id of preAttachFileIds) if (id && !set.has(id)) { set.add(id); changed = true; }
      return changed ? Array.from(set) : cur;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preKey]);

  // Sube cualquier documento → la IA lo lee y lo guarda en Files → aparece en el dropdown.
  async function onUpload(file: File) { await onUploadMany([file]); }

  /** VARIOS archivos de una (pedido del dueño: subir de a uno era un fastidio). Se suben en
   *  secuencia, todos quedan adjuntados, y se va viendo el avance (2/5…). Si alguno falla, los
   *  demás siguen — al final se dice cuáles no entraron. */
  async function onUploadMany(list: File[]) {
    if (!list.length) return;
    setUploading(true); setMsg(null);
    const failed: string[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      if (list.length > 1) setMsg(t('em.uploadingN', { i: String(i + 1), n: String(list.length) }));
      try {
        const key = await uploadFile(file, 'misc');
        const r = await api.post<{ id: string }>(`/api/claims/${claimId}/files`, { fileKey: key, fileName: file.name, mimeType: file.type });
        if (r.id) setAttachIds((ids) => (ids.includes(r.id) ? ids : [...ids, r.id])); // auto-adjunta
      } catch { failed.push(file.name); }
    }
    await loadFiles();
    setMsg(failed.length ? t('em.uploadFailedSome', { names: failed.join(', ') }) : t('em.uploaded'));
    setUploading(false);
  }

  async function load(which: Tab) {
    setLoading(true); setFallo(false);
    try { const r = await api.get<{ messages: EmailRow[] }>(`/api/claims/${claimId}/emails-thread?to=${which}${departmentKey ? `&dept=${departmentKey}` : ''}`); setRows(r.messages); }
    catch { setRows([]); setFallo(true); }
    setLoading(false);
  }

  // Reclasificar un email mal ubicado (ej. es del contratista pero cayó en Insurance): cambia su
  // recipient_type → salta al buzón/pestaña correcta. Recarga ambas listas (la actual lo pierde).
  const [recatBusy, setRecatBusy] = useState<string | null>(null);
  async function recategorize(emailId: string, cat: Tab) {
    if (cat === tab) return;
    setRecatBusy(emailId); setMsg(null);
    try {
      await api.post(`/api/emails/${emailId}/category`, { category: cat });
      setMsg(t('em.recatDone', { cat: t(`em.${cat}`) }));
      await load(tab); // el email ya no aparece en esta pestaña (se movió)
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); }
    finally { setRecatBusy(null); }
  }
  // Emails del destinatario (para prellenar "To" al cambiar de pestaña).
  const [recipients, setRecipients] = useState<{ contractor?: string | null; homeowner?: string | null }>({});
  const [insuranceEmail, setInsuranceEmail] = useState<string | null>(null); // del KB de aseguradoras
  const [insuranceSourceLoaded, setInsuranceSourceLoaded] = useState<string | null>(null); // de dónde salió (claim payload)
  // Insurance: sugiere los emails de los ajustadores (todos los que tengan) o, si no hay, el
  // email de comunicación del seguro (KB). Contractor/homeowner: su email del claim.
  // De dónde salió el correo del seguro que se ve en "To": lo dice la tarjeta (prop) o, en el claim, el
  // payload; si la sugerencia vino de los ajustadores cargados, es 'adjuster'.
  const fuenteDelCorreo: string | null = insuranceSource ?? (adjusters.some((a) => a.email) ? 'adjuster' : insuranceSourceLoaded);
  const insuranceSuggestion = (): string => {
    const adj = adjusters.filter((a) => a.email).map((a) => a.email!);
    return adj.length ? adj.join(', ') : (insuranceEmail ?? '');
  };
  const prefillFor = (tb: Tab): string =>
    (defaultTo?.[tb] ?? (tb === 'contractor' ? recipients.contractor : tb === 'homeowner' ? recipients.homeowner : tb === 'insurance' ? insuranceSuggestion() : null)) ?? '';
  useEffect(() => {
    load(tab); setMsg(null);
    // El To de una respuesta se aplica acá, después del prefill por pestaña (si no, el prefill lo pisa).
    if (toPendiente.current != null) { setTo(toPendiente.current); toPendiente.current = null; } else setTo(prefillFor(tab));
    /* eslint-disable-next-line */ }, [tab, claimId, recipients.contractor, recipients.homeowner]);
  function iniciarRespuesta(m: EmailLike) {
    const dest = destinoDeRespuesta(m);
    const cat: Tab = m.recipient_type === 'homeowner' || m.recipient_type === 'contractor' || m.recipient_type === 'insurance' ? m.recipient_type : tab;
    setReplyTo(m);
    setSubject(asuntoDeRespuesta(m.subject));
    if (cat !== tab) { toPendiente.current = dest; setTab(cat); } else setTo(dest);
  }
  // Los ajustadores / email del seguro cargan async: cuando llegan, rellenan (o MEJORAN) el "To"
  // del tab Insurance. Se sobreescribe solo si el campo está vacío o si tiene exactamente el email
  // del seguro (el sugerido "débil" → se mejora a los emails de los ajustadores). Nunca pisa lo
  // que el usuario tipeó a mano.
  useEffect(() => {
    if (tab !== 'insurance' || defaultTo?.insurance) return;
    const s = insuranceSuggestion();
    if (!s) return;
    setTo((cur) => (!cur.trim() || cur === (insuranceEmail ?? '\0') ? s : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjusters, insuranceEmail, tab]);
  // Trae datos del claim (dirección/emails para prellenar + nombre/RCV/etc. para los chips
  // y el subject cuando el componente se usa solo con claimId, ej. en los tabs del claim).
  const [cd, setCd] = useState<{ claim_number?: string | null; homeowner_name?: string | null; contractor_name?: string | null; rcv_amount_cents?: number | null; acv_cents?: number | null; insurance_company?: string | null }>({});
  useEffect(() => {
    api.get<{ address?: string | null; homeowner_email?: string | null; contractor_email?: string | null; claim_number?: string | null; homeowner_name?: string | null; contractor_name?: string | null; rcv_amount_cents?: number | null; acv_cents?: number | null; insurance_company?: string | null; insurance_email?: string | null; insurance_email_source?: string | null }>(`/api/claims/${claimId}`)
      .then((r) => {
        setAddress(r.address ?? null);
        setRecipients({ contractor: r.contractor_email ?? null, homeowner: r.homeowner_email ?? null });
        setInsuranceEmail(r.insurance_email ?? null);
        setInsuranceSourceLoaded(r.insurance_email_source ?? null);
        setCd({ claim_number: r.claim_number, homeowner_name: r.homeowner_name, contractor_name: r.contractor_name, rcv_amount_cents: r.rcv_amount_cents, acv_cents: r.acv_cents, insurance_company: r.insurance_company });
        if (!claimNumber && !subject && r.claim_number) setSubject(r.claim_number);
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);
  const effClaimNumber = claimNumber ?? cd.claim_number ?? null;
  const effTokens: EmailTokens = {
    name: tokens?.name ?? cd.homeowner_name,
    contractor: tokens?.contractor ?? cd.contractor_name,
    rcv: tokens?.rcv ?? (cd.rcv_amount_cents != null ? money(cd.rcv_amount_cents / 100) : null),
    insurance: tokens?.insurance ?? cd.insurance_company,
  };

  // Inserta un "chip" (etiqueta con borde de color + valor real) en la posición del cursor.
  function insertChip(value: string) {
    if (!value) return;
    editorRef.current?.insertHtml(`<span contenteditable="false" style="border:1px solid #a855f7;border-radius:5px;padding:0 5px;margin:0 1px;color:#7c3aed;background:#f5f3ff">${value.replace(/</g, '&lt;')}</span>&nbsp;`);
  }

  // Inserta HTML crudo (ej. el resumen "Proactive vs Insurance") en el cursor.
  function insertHtml(html: string) {
    if (!html) return;
    editorRef.current?.insertHtml(`${html}<br>`);
  }

  const CHIPS: { label: string; value: string | null | undefined }[] = [
    { label: t('em.tName'), value: effTokens.name },
    { label: t('em.tAddress'), value: address },
    { label: t('em.tContractor'), value: effTokens.contractor },
    { label: t('em.tRcv'), value: effTokens.rcv },
    { label: t('em.tClaim'), value: effClaimNumber },
    { label: t('em.tInsurance'), value: effTokens.insurance },
  ];

  // Direcciones en copia según los checks. Nunca se copia a quien ya es el destinatario
  // (el backend también lo filtra, pero acá se evita mostrarlo).
  function ccValue(): string {
    const list: string[] = [];
    if (ccHomeowner && recipients.homeowner) list.push(recipients.homeowner);
    if (ccContractor && recipients.contractor) list.push(recipients.contractor);
    const already = to.toLowerCase();
    return list.filter((e) => !already.includes(e.toLowerCase())).join(', ');
  }

  /**
   * Carga una plantilla de la vista: rellena asunto y cuerpo, REEMPLAZANDO los placeholders
   * ({name}, {address}, {contractor}, {rcv}, {claim}, {insurance} — los chips del editor de
   * plantillas) por los datos reales de ESTE claim. Sin dato → el placeholder se quita.
   */
  function applyTemplate(id: string) {
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    const values: Record<string, string> = {
      name: effTokens.name ?? '',
      address: address ?? '',
      contractor: effTokens.contractor ?? '',
      rcv: effTokens.rcv ?? '',
      acv: cd.acv_cents != null ? money(cd.acv_cents / 100) : '',
      claim: effClaimNumber ?? '',
      insurance: effTokens.insurance ?? '',
    };
    const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let html = tpl.body_html || '';
    // Chips del editor de plantillas (data-ph) → el valor real, con el mismo chip morado del email.
    html = html.replace(/<span[^>]*data-ph="([a-z]+)"[^>]*>.*?<\/span>/gi, (_m, ph: string) => {
      const v = values[ph];
      if (!v) return '';
      return `<span contenteditable="false" style="border:1px solid #a855f7;border-radius:5px;padding:0 5px;margin:0 1px;color:#7c3aed;background:#f5f3ff">${esc(v)}</span>`;
    });
    // Placeholders escritos a mano como texto ({name}, {claim}…) — también en el asunto.
    const subst = (text: string) => text.replace(/\{(name|address|contractor|rcv|acv|claim|insurance)\}/gi, (_m, ph: string) => values[ph.toLowerCase()] ?? '');
    html = subst(html);
    if (tpl.subject?.trim()) setSubject(subst(tpl.subject.trim()));
    editorRef.current?.clear();
    editorRef.current?.insertHtml(html);
  }

  /**
   * LA PLANTILLA QUE SE CARGA SOLA (pedido del dueño 2026-09-01: "cuando suban el estimado quiero
   * q automaticamente este attached al email y se seleccione la plantilla de first estimate").
   *
   * Se busca por NOMBRE porque los ids de las plantillas son de cada empresa. Dos frenos: se
   * aplica UNA sola vez por claim y solo si el cuerpo está VACÍO — el editor guarda borradores,
   * así que pisar el cuerpo le borraría a alguien un email a medio escribir para poner uno
   * genérico. Si la plantilla no existe (la renombraron, otra empresa) no pasa nada y se elige a
   * mano, que es como funcionaba antes.
   */
  /**
   * PRECARGA DEL EMAIL YA ESCRITO (`prefill`). Misma prudencia que la plantilla automática: solo
   * sobre un cuerpo vacío… o sobre un cuerpo que sigue siendo exactamente lo sembrado la vez
   * anterior (la huella vive en localStorage porque el borrador del editor también sobrevive al
   * desmontaje: si no, al reabrir la tarjeta el borrador "lleno" bloquearía la actualización de
   * un email que suplementos reescribió). El subject se toma si está vacío, es el claim number por
   * defecto o es el que se sembró antes.
   */
  const prefillKey = `qe:${claimId}:${departmentKey ?? 'main'}:prefill`;
  useEffect(() => {
    const fuente = (prefill?.html ?? '').trim() || (prefill?.text ?? '').trim();
    if (!fuente || !editorRef.current) return;
    type Huella = { src: string; html: string; subject: string | null };
    let previa: Huella | null = null;
    try { const raw = localStorage.getItem(prefillKey); previa = raw ? (JSON.parse(raw) as Huella) : null; } catch { previa = null; }
    const actualHtml = editorRef.current.getHtml();
    const vacio = !(editorRef.current.getText() ?? '').trim();
    if (previa?.src === fuente && !vacio) return; // ya está sembrado y nadie lo borró
    if (!vacio && previa?.html !== actualHtml) return; // lo escribió una persona: no se toca
    const html = (prefill?.html ?? '').trim() || textoAHtml(prefill?.text ?? '');
    editorRef.current.clear();
    editorRef.current.insertHtml(html);
    const nuevoSubject = (prefill?.subject ?? '').trim() || null;
    const defecto = (claimNumber ?? '').trim();
    if (nuevoSubject && (!subject.trim() || subject.trim() === defecto || subject === previa?.subject)) setSubject(nuevoSubject);
    try { localStorage.setItem(prefillKey, JSON.stringify({ src: fuente, html: editorRef.current.getHtml(), subject: nuevoSubject } satisfies Huella)); } catch { /* sin localStorage no hay huella: se vuelve a sembrar solo si queda vacío */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.text, prefill?.html, prefill?.subject, claimId]);

  const autoTplHecho = useRef<string | null>(null);
  useEffect(() => {
    if (!autoTemplate || !templates.length) return;
    const marca = `${claimId}:${autoTemplate}`;
    if (autoTplHecho.current === marca) return;
    const norm = (x: string) => x.trim().toLowerCase();
    const tpl = templates.find((x) => norm(x.name) === norm(autoTemplate));
    if (!tpl) { autoTplHecho.current = marca; return; }
    if ((editorRef.current?.getText() ?? '').trim()) { autoTplHecho.current = marca; return; }
    autoTplHecho.current = marca;
    applyTemplate(tpl.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTemplate, templates, claimId]);

  async function send() {
    let body = editorRef.current?.getText() ?? '';
    let bodyHtml = editorRef.current?.getHtml() ?? '';
    if (!to.trim() || !subject.trim()) { setMsg(t('qe.needFields')); return; }
    // ATR: el link del video aceptado se INCLUYE en el cuerpo del email al adjuster.
    if (atrVideo?.url) {
      const url = atrVideo.url;
      const safe = url.replace(/"/g, '%22').replace(/</g, '%3C');
      const text = atrVideo.label ? `${atrVideo.label}: ${url}` : url;
      body = `${body}\n\nATR video: ${text}`;
      bodyHtml = `${bodyHtml || ''}<p>ATR video: <a href="${safe}">${(atrVideo.label || url).replace(/</g, '&lt;')}</a></p>`;
    }
    setBusy(true); setMsg(null);
    const freno = beforeSend ? await beforeSend(tab) : null;
    if (freno) { setMsg(freno); setBusy(false); return; }
    const attach = [attachSup && canAttach?.supplement ? 'supplement' : null, attachIns && canAttach?.insurance ? 'insurance' : null].filter(Boolean);
    /**
     * ENVIAR CON DESHACER (usuarios, 2026-10-08): el correo se ENCOLA 10 s con una ventanita abajo a
     * la izquierda; Undo lo saca de la cola y devuelve el borrador al cuadro. Cambiar de pantalla lo
     * manda ya. El cuadro se limpia al momento para que se sienta enviado; `onSent` (lo que avanza
     * el claim) corre recién cuando salió de verdad.
     */
    const foto = { to: to.trim(), cc: ccValue() || null, subject: subject.trim(), body, bodyHtml, attach, attachIds: [...attachIds], tab, desde, miCasilla, editorHtml: editorRef.current?.getHtml() ?? '', replyTo };
    const porMiCasilla = desde === 'yo' && !!miCasilla;
    editorRef.current?.clear();
    setAttachIds([]); setBusy(false); setMsg(t('undo.queued')); setReplyTo(null);
    encolarEnvio({
      etiqueta: `${t('undo.emailTo')} ${foto.to}`,
      ejecutar: async (opts) => {
        if (porMiCasilla) {
          // Por MI casilla: sale con mi dirección real y sin token. El backend lo escribe igual en
          // claim_emails, así que aparece en esta misma pestaña.
          await api.post('/api/mailbox/send', { claimId, to: foto.to, cc: foto.cc, subject: foto.subject, body: foto.body, recipientType: foto.tab, replyToEmailId: foto.replyTo?.id ?? null }, opts);
        } else {
          await api.post(`/api/claims/${claimId}/emails-thread`, { replyToEmailId: foto.replyTo?.id ?? null, recipientType: foto.tab, to: foto.to, cc: foto.cc, subject: foto.subject, body: foto.body, bodyHtml: foto.bodyHtml.trim() || null, attach: foto.attach, attachFileIds: foto.attachIds, departmentKey: departmentKey ?? null, alsoClaimIds: alsoClaimIds && alsoClaimIds.length ? alsoClaimIds : undefined }, opts);
        }
      },
      deshacer: () => {
        editorRef.current?.clear(); editorRef.current?.insertHtml(foto.editorHtml);
        setAttachIds(foto.attachIds); setSubject(foto.subject); setTo(foto.to); setReplyTo(foto.replyTo); setMsg(t('undo.undone'));
      },
      alTerminar: (ok) => { if (ok) { setMsg(t('qe.sent')); void load(foto.tab); onSent?.(foto.tab); } },
    });
  }

  const TABS: { k: Tab; label: string }[] = [
    { k: 'insurance', label: t('em.insurance') }, { k: 'contractor', label: t('em.contractor') }, { k: 'homeowner', label: t('em.homeowner') },
  ];

  return (
    // Cuando el padre manda `side` (p. ej. el follow-up del depto), la pantalla se parte MITAD y
    // MITAD (.email-split): en una columna angosta los updates quedaban ilegibles. Sin `side`, o
    // en pantallas chicas, va a ancho completo y lo lateral se apila debajo.
    <div className={`email-split${side ? ' has-side' : ''}`} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 10 }}>
      <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {title ?? t('em.title')}</div>
      {/* Pestañas por destinatario + chips de datos a la derecha. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {TABS.map((x) => <button key={x.k} className={`btn sm ${tab === x.k ? '' : 'ghost'}`} onClick={() => setTab(x.k)}>{x.label}</button>)}
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {CHIPS.filter((c) => c.value).map((c) => (
            <button key={c.label} type="button" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', borderColor: '#a855f7', color: '#7c3aed' }}
              title={c.value ?? ''} onClick={() => insertChip(c.value!)}>+ {c.label}</button>
          ))}
          {(extraChips ?? []).filter((c) => c.value).map((c) => (
            <button key={c.label} type="button" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 'var(--fw-bold)' }}
              title={c.label} onClick={() => insertHtml(c.value)}>+ {c.label}</button>
          ))}
        </div>
      </div>
      {/* Historial (envíos + respuestas): el hilo UNIFICADO de toda la app (components/email/
          EmailThread): conversaciones, quién → a quién, seguimiento de apertura arriba, más reciente
          primero, cada correo se recoge/despliega, pantalla completa. Arranca RECOGIDO —este box
          vive en muchas filas de las colas— y recuerda por departamento si lo dejaste abierto. */}
      {!hideHistory && (
        <EmailThread messages={rows} loading={loading} error={fallo} onRetry={() => { void load(tab); }}
          emptyText={t('em.noHistory')} collapsible persistKey={`box:${departmentKey ?? 'main'}`} defaultOpenPanel={false}
          onRecategorize={recategorize} onAddToFiles={addToClaimFiles} onReply={iniciarRespuesta} busyId={savingAtt ?? recatBusy} />
      )}
      {replyTo && (
        <div className="et-replying">
          <span>↩ {t('et.replyingTo', { who: (replyTo.direction === 'out' ? replyTo.to_address : (replyTo.from_name || replyTo.from_address)) || '—', when: new Date(replyTo.created_at).toLocaleString() })}</span>
          <button type="button" className="x" onClick={() => setReplyTo(null)}>✕ {t('et.cancelReply')}</button>
        </div>
      )}
      {/* Composer: to, subject (claim#), mensaje con chips. */}
      {/* Elegir el email de un ajustador — SOLO en la pestaña Insurance (no homeowner/contractor). */}
      {tab === 'insurance' && adjusters.some((a) => a.email) && (
        <select className="input sm" value="" onChange={(e) => { if (e.target.value) setTo((cur) => addRecipient(cur, e.target.value)); }}>
          <option value="">{t('em.pickAdjuster')}…</option>
          {adjusters.filter((a) => a.email).map((a) => (
            <option key={a.id} value={a.email!}>{adjusterTitleShort(a)} · {a.name || a.email} — {a.email} ({fmtDate(a.created_at)})</option>
          ))}
        </select>
      )}
      <input className="input sm" placeholder={t('qe.toMulti')} value={to} onChange={(e) => setTo(e.target.value)} />
      <RecipientChips to={to} />
      {/* DE DÓNDE SALIÓ EL CORREO DEL SEGURO, o por qué no hay (caso Gilmore 2026-09-28: la ficha de
          Horace Mann no tenía el buzón de reclamos y el botón de enviar quedaba muerto sin explicar). */}
      {tab === 'insurance' && !to.trim() && (
        <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{t('qe.insEmailMissing')}</span>
          {cd.insurance_company && <span onClick={(e) => e.stopPropagation()}><InsuranceLink name={cd.insurance_company} /></span>}
        </div>
      )}
      {tab === 'insurance' && to.trim() && fuenteDelCorreo === 'adjuster' && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('qe.insEmailFromAdjuster')}</div>}
      {tab === 'insurance' && to.trim() && fuenteDelCorreo === 'kb_details' && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>{t('qe.insEmailFromDetails')}</div>}
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: -4 }}>{t('qe.toMultiHint')}</div>
      {/* COPIA — el homeowner y el contratista reciben el mismo email sin ser el destinatario.
          Solo se ofrece a quien tiene email cargado en el claim. */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-2xs)' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)' }}>{t('em.ccLabel')}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: recipients.homeowner ? 'pointer' : 'not-allowed', opacity: recipients.homeowner ? 1 : 0.5 }}
          title={recipients.homeowner ?? t('em.ccNoEmail')}>
          <input type="checkbox" disabled={!recipients.homeowner} checked={ccHomeowner} onChange={(e) => setCcHomeowner(e.target.checked)} />
          <Home size={14} strokeWidth={1.75} /> {t('em.homeowner')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: recipients.contractor ? 'pointer' : 'not-allowed', opacity: recipients.contractor ? 1 : 0.5 }}
          title={recipients.contractor ?? t('em.ccNoEmail')}>
          <input type="checkbox" disabled={!recipients.contractor} checked={ccContractor} onChange={(e) => setCcContractor(e.target.checked)} />
          <Wrench size={14} strokeWidth={1.75} /> {t('em.contractor')}
        </label>
        {ccValue() && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('em.ccTo', { list: ccValue() })}</span>}
      </div>
      <input className="input sm" placeholder={t('qe.subject')} value={subject} onChange={(e) => setSubject(e.target.value)} />
      {/* Plantillas de ESTA vista: rellenan asunto y cuerpo. Se crean en el ⚙ de la vista. */}
      {/* Ya no es un <select> nativo: Chrome lo pintaba corrido y Gabriela veía la mitad de cada nombre
          (2026-10-28). Es nuestra ventana centrada (TemplatePickerModal), con grupos y buscador. */}
      {templates.length > 0 && (
        <button type="button" className="btn ghost sm" style={{ justifySelf: 'start' }} onClick={() => setTplOpen(true)}>
          {t('em.useTemplate')}…
        </button>
      )}
      {tplOpen && (
        <TemplatePickerModal onClose={() => setTplOpen(false)} onPick={(id) => { applyTemplate(id); setTplOpen(false); }}
          groups={(() => {
            // Agrupadas: primero las de MI departamento, luego las globales/de la vista, luego
            // los demás deptos — cualquiera puede usar la plantilla de otro depto.
            const grupos = new Map<string, typeof templates>();
            for (const tpl of templates) {
              const g = tpl.dept === undefined ? '__vista__' : (tpl.dept ?? '__global__');
              if (!grupos.has(g)) grupos.set(g, []);
              grupos.get(g)!.push(tpl);
            }
            const orden = [...grupos.keys()].sort((a, b) => {
              const peso = (k: string) => (k === departmentKey ? 0 : k === '__vista__' ? 1 : k === '__global__' ? 2 : 3);
              return peso(a) - peso(b) || a.localeCompare(b);
            });
            const nombre = (k: string) => (k === '__vista__' ? t('em.tplFromView') : k === '__global__' ? t('tpl.allDepts') : t(`dept.${k}`));
            return orden.map((k) => ({ key: k, label: nombre(k), templates: grupos.get(k)!.map((tpl) => ({ id: tpl.id, name: tpl.name, subject: tpl.subject ?? null })) }));
          })()} />
      )}
      <RichEditor ref={editorRef} minHeight={64} placeholder={t('qe.bodyPh')} draftKey={`email:${claimId}:${departmentKey ?? 'main'}`} />
      {/* ATR: aviso de que el link del video aceptado se incluirá en el email. */}
      {atrVideo?.url && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)', border: '1px solid var(--green)', borderRadius: 8, padding: '5px 9px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Video size={14} strokeWidth={1.75} /> {t('atr.videoIncluded')}:</span>
          <a href={atrVideo.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{atrVideo.label || atrVideo.url}</a>
        </div>
      )}
      {/* Adjuntos con checkmark (elige cuál mandar) + enviar. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn sm" disabled={busy || !to.trim() || !subject.trim()} onClick={send}>{t('qe.send')}</button>
        {miCasilla && (
          <div style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--surface-2)', borderRadius: 9 }}>
            {(['sistema', 'yo'] as const).map((k) => (
              <button key={k} onClick={() => setDesde(k)}
                title={k === 'yo' ? miCasilla.email : t('comms.fromSystemHint')}
                style={{ fontSize: 'var(--fs-2xs)', padding: '4px 9px', border: 0, borderRadius: 7, cursor: 'pointer',
                  fontWeight: desde === k ? 700 : 500,
                  background: desde === k ? 'var(--card)' : 'transparent',
                  color: desde === k ? 'var(--accent)' : 'var(--muted)' }}>
                {k === 'sistema' ? t('comms.fromSystem') : <><Mailbox size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('comms.fromMe')}</>}
              </button>
            ))}
          </div>
        )}
        {miCasilla && desde === 'yo' && (
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t('comms.fromMeNote', { email: miCasilla.email })}
            {/* El badge de ENVIAR, con su fecha: acá es el único canal que importa. */}
            <MailboxChannelBadge canal="smtp" estado={miCasilla.smtp} compacto />
          </span>
        )}
        {canAttach?.supplement && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-2xs)', cursor: 'pointer' }}>
            <input type="checkbox" checked={attachSup} onChange={(e) => setAttachSup(e.target.checked)} /> <Paperclip size={14} strokeWidth={1.75} /> {t('em.attachSupplement')}
          </label>
        )}
        {canAttach?.insurance && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-2xs)', cursor: 'pointer' }}>
            <input type="checkbox" checked={attachIns} onChange={(e) => setAttachIns(e.target.checked)} /> <Paperclip size={14} strokeWidth={1.75} /> {t('em.attachInsurance')}
          </label>
        )}
        {msg && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</span>}
      </div>
      {/* Adjuntar archivos del claim (PDF/ESX) desde un dropdown + subir cualquier documento
          (la IA lo lee y lo guarda en Files → queda disponible aquí). */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        {/* Adjuntar desde Files: la ventana centrada y grande (AttachFilesModal, dueño 2026-10-28), con
            los archivos por grupo, monto, fecha y quién. El dropdown chico de antes se quedaba sin
            los invoices (nunca estaban en Files, mig 0552) y "abría muy pequeño". */}
        <button type="button" className="btn ghost sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setFilesOpen(true)}>
          <Paperclip size={14} strokeWidth={1.75} /> {t('em.attachFile')}…
        </button>
        {filesOpen && (
          <AttachFilesModal claimId={claimId} selected={attachIds} onClose={() => setFilesOpen(false)}
            onAttach={(elegidos) => {
              setFiles((cur) => {
                const ids = new Set(cur.map((f) => f.id));
                const nuevos = elegidos.filter((f) => !ids.has(f.id)).map((f) => ({ ...f, doc_type: f.doc_type ?? 'other' }) as ClaimFile);
                return nuevos.length ? [...nuevos, ...cur] : cur;
              });
              setAttachIds((a) => [...a, ...elegidos.map((f) => f.id).filter((id) => !a.includes(id))]);
            }} />
        )}
        <div style={{ minWidth: 200, flex: 1, border: '1.5px dashed var(--line)', borderRadius: 8, padding: 6, background: 'var(--surface-2)' }}>
          <FileDrop onFile={onUpload} onFiles={onUploadMany} multiple accept="application/pdf,.esx,.ESX,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg,.zip" label={t('em.uploadDoc')} busy={uploading} busyLabel={t('cd.reading')} compact />
        </div>
      </div>
      {attachIds.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {attachIds.map((id) => {
            const f = files.find((x) => x.id === id);
            return (
              <span key={id} className="badge" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)' }}>
                <Paperclip size={12} strokeWidth={1.75} /> {f?.file_name || id}
                <button type="button" onClick={() => setAttachIds((a) => a.filter((x) => x !== id))} style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}><X size={12} strokeWidth={1.75} /></button>
              </span>
            );
          })}
        </div>
      )}
      </div>
      {side && <div style={{ minWidth: 0 }}>{side}</div>}
    </div>
  );
}
